import React from 'react';
import {
  ArrowDownRight,
  ArrowUpRight,
  CircleDollarSign,
  Receipt,
  TrendingDown,
  TrendingUp,
  WalletCards,
} from 'lucide-react';

const formatPercentage = (value) => {
  if (value == null || Number.isNaN(Number(value))) return null;
  return `${Math.abs(Number(value)).toFixed(1)}%`;
};

const VariationBadge = ({ value, suffix = '' }) => {
  if (value == null || Number.isNaN(Number(value))) {
    return (
      <span className="text-xs text-gray-500">
        Sem base comparativa
      </span>
    );
  }

  const numericValue = Number(value);
  const positive = numericValue > 0;
  const negative = numericValue < 0;
  const Icon = positive ? ArrowUpRight : negative ? ArrowDownRight : null;

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${
        positive
          ? 'bg-emerald-50 text-emerald-700'
          : negative
          ? 'bg-red-50 text-red-700'
          : 'bg-gray-100 text-gray-600'
      }`}
    >
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {numericValue === 0 ? 'Estável' : `${formatPercentage(numericValue)}${suffix}`}
    </span>
  );
};

const BillingStatsCards = ({ stats, financialReport, formatCurrency }) => {
  const comparison = financialReport?.comparison || {};
  const invoiced = Number(financialReport?.total_invoiced || 0);
  const received = Number(financialReport?.total_received ?? stats?.period_revenue ?? 0);
  const expensesPaid = Number(
    financialReport?.total_expenses_paid ?? stats?.total_expenses_paid ?? 0
  );
  const operatingResult = Number(
    financialReport?.operating_result ?? stats?.operating_result ?? received - expensesPaid
  );

  const cards = [
    {
      label: 'Faturado no período',
      value: invoiced,
      helper: comparison.available
        ? `Anterior: ${formatCurrency(comparison.previous_invoiced)}`
        : 'Total de faturas e receitas emitidas',
      variation: comparison.available
        ? comparison.invoiced_change_percentage
        : null,
      icon: CircleDollarSign,
      iconClass: 'bg-purple-50 text-purple-700',
    },
    {
      label: 'Recebido no período',
      value: received,
      helper: comparison.available
        ? `Anterior: ${formatCurrency(comparison.previous_received)}`
        : 'Entradas efetivamente confirmadas',
      variation: comparison.available
        ? comparison.received_change_percentage
        : null,
      icon: WalletCards,
      iconClass: 'bg-emerald-50 text-emerald-700',
    },
    {
      label: 'Despesas pagas',
      value: expensesPaid,
      helper: 'Pagamentos de despesas confirmados',
      variation: null,
      icon: Receipt,
      iconClass: 'bg-red-50 text-red-700',
    },
    {
      label: 'Resultado operacional',
      value: operatingResult,
      helper: 'Recebimentos menos despesas pagas',
      variation: null,
      icon: operatingResult >= 0 ? TrendingUp : TrendingDown,
      iconClass:
        operatingResult >= 0
          ? 'bg-blue-50 text-blue-700'
          : 'bg-red-50 text-red-700',
      valueClass:
        operatingResult >= 0 ? 'text-blue-700' : 'text-red-700',
    },
  ];

  return (
    <section>
      <div className="mb-3">
        <h2 className="text-lg font-semibold text-gray-900">
          Visão executiva
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          Principais indicadores financeiros do período selecionado.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => {
          const Icon = card.icon;

          return (
            <div
              key={card.label}
              className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
            >
              <div className="flex items-start justify-between gap-4">
                <div className={`rounded-lg p-2.5 ${card.iconClass}`}>
                  <Icon className="h-5 w-5" />
                </div>

                {card.variation != null && (
                  <VariationBadge value={card.variation} />
                )}
              </div>

              <p className="mt-4 text-sm font-medium text-gray-600">
                {card.label}
              </p>

              <p
                className={`mt-1 text-2xl font-bold ${
                  card.valueClass || 'text-gray-900'
                }`}
              >
                {formatCurrency(card.value)}
              </p>

              <p className="mt-2 text-xs leading-5 text-gray-500">
                {card.helper}
              </p>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default BillingStatsCards;
